import React from 'react';
import { Instance3D, Model3DXML, Reference3D } from '@gkn-ili/cad-model';
import { useAppState } from '../../../context';
import { applyConfigurationRules, refIsEqual, View3DConfigurationRule } from '../../../context/context3D/model3dState';
import { Button, Input, Tree, TreeItem } from '@fluentui/react-components';
import {
  Comment16Regular,
  CommentOff16Regular,
  Eye16Regular,
  EyeOff16Regular,
  HandRight16Regular,
  HandRightOff16Regular,
} from '@fluentui/react-icons';
import { AppActivityConfigFormFields } from './activityConfig';

type GridFieldFunction<TValue extends string | boolean | number> = (reference: Instance3D, ref: string[]) => TValue;
type GridFieldGenerator<TValue extends string | boolean | number, TKey extends string = string> = {
  fieldFunc: GridFieldFunction<TValue>;
  key: TKey;
  headerName: string;
  width?: number;
  cellRenderer?: (props: { value: TValue; data: GridRow & Record<TKey, TValue> }) => JSX.Element;
  onCellClick?: (row: GridRow & Record<TKey, TValue>) => unknown;
  editable: false | true | ((params: { data: GridRow & Record<TKey, TValue> }) => boolean);
  cellEditRequestEvent?: (data: GridRow & Record<TKey, TValue>, newValue: TValue) => unknown;
};

interface GridRow {
  id: number;
  name: string;
  ref: string[];
  children: (GridRow & Record<string, unknown>)[];
}

export const defaultFieldGenerators = {
  hideShow: (
    rules: View3DConfigurationRule[],
    onHideShow: (ref: string[], newState: boolean) => unknown,
  ): GridFieldGenerator<boolean, 'visible'> => ({
    key: 'visible',
    headerName: '',
    fieldFunc: (_, ref) => {
      const state = applyConfigurationRules(ref, rules);

      return state.visible;
    },
    onCellClick: (row) => onHideShow(row.ref, !row.visible),
    cellRenderer: (props: { value: boolean; data: GridRow }) => {
      return (
        <Button
          aria-label="editVisibility"
          appearance="subtle"
          icon={props.value ? <Eye16Regular /> : <EyeOff16Regular />}
        />
      );
    },
    editable: false,
    width: 48,
  }),
  toggleActive: (
    activeReferences: AppActivityConfigFormFields['activity']['subjects'],
    onSelect: (ref: string[], newActiveState: boolean) => unknown,
  ): GridFieldGenerator<boolean, 'active'> => ({
    key: 'active',
    headerName: '',
    fieldFunc: (_, ref) => activeReferences.some((aRef) => refIsEqual(aRef.ref, ref)),
    onCellClick: (row) => {
      onSelect(row.ref, !row.active);
    },
    cellRenderer: (props: { value: boolean; data: GridRow }) => {
      return (
        <Button
          aria-label="editActive"
          appearance="subtle"
          icon={props.value ? <HandRight16Regular /> : <HandRightOff16Regular />}
        />
      );
    },
    editable: false,
    width: 48,
  }),
  toggleAnnotation: (
    activeReferences: AppActivityConfigFormFields['activity']['subjects'],
    onUpdate: (ref: string[], newAnnotationState: boolean) => unknown,
  ): GridFieldGenerator<'disabled' | true | false, 'annotation'> => ({
    key: 'annotation',
    headerName: '',
    fieldFunc: (_, ref) => {
      const subject = activeReferences.find((k) => refIsEqual(k.ref, ref));
      if (!subject) {
        return 'disabled';
      }
      return subject.showPartNumber;
    },
    onCellClick: (row) => onUpdate(row.ref, !row.annotation),
    cellRenderer: (props) => {
      if (props.value === 'disabled') {
        return <></>;
      }
      return (
        <Button
          aria-label="toggleAnnotation"
          appearance="subtle"
          icon={props.value ? <Comment16Regular /> : <CommentOff16Regular />}
        />
      );
    },
    editable: false,
    width: 48,
  }),
  editOffsetPosition: (
    activeReferences: AppActivityConfigFormFields['activity']['subjects'],
    onUpdate: (ref: string[], newOffsetPosition: string) => unknown,
  ): GridFieldGenerator<string, 'offsetPosition'> => ({
    key: 'offsetPosition',
    headerName: 'offsetPosition',
    fieldFunc: (_, ref) => {
      const item = activeReferences.find((k) => refIsEqual(k.ref, ref));
      if (!item) {
        return '';
      }
      return item.offsetPosition;
    },
    editable: (params) => {
      return params.data.offsetPosition !== '';
    },
    cellEditRequestEvent: (data, newValue) => {
      const match = RegExp('^-?[0-9]+(.[0-9]+)?;-?[0-9]+(.[0-9]+)?;-?[0-9]+(.[0-9]+)?$', 'm').test(newValue);
      if (match) {
        onUpdate(data.ref, newValue);
      }
    },
    width: 128,
  }),
};

function createGridRows(model: Model3DXML, fieldGenerators: GridFieldGenerator<string | number | boolean>[]) {
  function iterateReference(
    ref3D: Reference3D,
    fieldGenerators: GridFieldGenerator<string | number | boolean>[],
    ref: string[],
  ): (GridRow & Record<string, unknown>)[] {
    return Object.values(ref3D.children).map((value) => ({
      id: value.id,
      name: value.name,
      ref: [...ref, value.name],
      partId: value.instance.id,
      partName: value.instance.name,
      children: iterateReference(value.instance, fieldGenerators, [...ref, value.name]),
      ...fieldGenerators.reduce(
        (result, generator) => ({
          ...result,
          [generator.key]: generator.fieldFunc(value, [...ref, value.name]),
        }),
        {},
      ),
    }));
  }
  return iterateReference(model, fieldGenerators, [model.name]);
}

function AppTreeRow(props: {
  row: GridRow & Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldGenerators: GridFieldGenerator<any>[];
}): JSX.Element {
  return (
    <TreeItem itemType={props.row.children.length > 0 ? 'branch' : 'leaf'}>
      <div style={{ display: 'flex', flexDirection: 'row', alignContent: 'flex-start' }}>
        <div style={{ flexGrow: 1 }}>{props.row.name}</div>
        {props.fieldGenerators.map((fieldGenerator) => (
          <div
            style={{ flexGrow: 0, width: fieldGenerator.width }}
            key={fieldGenerator.key}
            onClick={() => fieldGenerator.onCellClick?.(props.row)}
          >
            {fieldGenerator.cellRenderer ? (
              fieldGenerator.cellRenderer({ value: props.row[fieldGenerator.key], data: props.row })
            ) : fieldGenerator.editable === true ||
              (fieldGenerator.editable !== false && fieldGenerator.editable?.({ data: props.row })) ? (
              <Input
                style={{ width: 96 }}
                onClick={(ev) => ev.stopPropagation()}
                onBlur={(ev) => fieldGenerator.cellEditRequestEvent?.(props.row, ev.target.value)}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                defaultValue={props.row[fieldGenerator.key] as any}
              />
            ) : (
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (props.row[fieldGenerator.key] as any)
            )}
          </div>
        ))}
      </div>
      <Tree aria-label="default layout">
        {props.row.children.map((value) => (
          <AppTreeRow key={value.id} row={value} fieldGenerators={props.fieldGenerators} />
        ))}
      </Tree>
    </TreeItem>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function AppModelTreeGrid(props: { fieldGenerators: GridFieldGenerator<any>[] }): JSX.Element {
  const appState = useAppState();
  const model = appState.model;

  const rows: (GridRow & Record<string, unknown>)[] = model
    ? createGridRows(model, props.fieldGenerators)
    : [{ name: 'No model loaded', id: -1, ref: [], children: [] }];

  return (
    <Tree aria-label="default layout">
      {rows.map((row) => (
        <AppTreeRow key={row.id} row={row} fieldGenerators={props.fieldGenerators} />
      ))}
    </Tree>
  );
}
